import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  X,
  Building2,
  User,
  Mail,
  Phone,
  IndianRupee,
  CalendarClock,
  IdCard,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "./ui/input";
import DropdownFilter from "./DropdownFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CustomerDetail } from "../types/prospect";
import {
  CustomerValidDesignations,
  CustomerValidEntityTypes,
} from "../constants";
import apiService from "../services/apiService";
import { toast } from "react-toastify";
interface AddCustomerPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (customerData: Partial<CustomerDetail>) => Promise<boolean>;
  admin_users: AdminUserList[];
  lender_user_product_types: LenderUserProductType[];
  isEdit?: boolean;
  customerId?: string | null;
  initialData?: CustomerDetail | null;
}

interface LenderUserProductType {
  id: number;
  name: string;
  display_order: number;
  parent_type: string;
}

interface AdminUserList {
  id: number;
  name: string;
}

interface InputWithIconProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon: React.ReactNode;
  hasError?: boolean;
}

const InputWithIcon: React.FC<InputWithIconProps> = ({
  icon,
  className,
  hasError = false,
  ...props
}) => (
  <div className="relative">
    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
      {icon}
    </span>
    <Input
      {...props}
      className={`${hasError ? "border-destructive" : ""} h-9 pl-10 ${className ?? ""}`.trim()}
    />
  </div>
);

/** Indian income-tax PAN: 5 letters, 4 digits, 1 letter */
const INDIAN_PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

function extractPnpLegalName(res: unknown): string {
  const fromPnp = (pnp: unknown): string => {
    if (!pnp || typeof pnp !== "object") return "";
    const raw = (pnp as Record<string, unknown>)["legal_name"];
    return typeof raw === "string" ? raw.trim() : "";
  };

  if (!res || typeof res !== "object") return "";
  const root = res as Record<string, unknown>;

  const direct = fromPnp(root["pnp"]);
  if (direct) return direct;

  const data = root["data"];
  if (data && typeof data === "object") {
    const d = data as Record<string, unknown>;
    const nested = fromPnp(d["pnp"]);
    if (nested) return nested;
    const inner = d["data"];
    if (inner && typeof inner === "object") {
      return fromPnp((inner as Record<string, unknown>)["pnp"]);
    }
  }
  return "";
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface FormValidationInput {
  formData: Partial<CustomerDetail>;
  isEdit: boolean;
  requirementType: string;
  dealTypeId: string;
  showPanField: boolean;
  panTrimmed: string;
  entityNameFromPnpFetch: boolean;
}

function collectFormValidationErrors(
  input: FormValidationInput
): Record<string, string> {
  const {
    formData,
    isEdit,
    requirementType,
    dealTypeId,
    showPanField,
    panTrimmed,
    entityNameFromPnpFetch,
  } = input;
  const newErrors: Record<string, string> = {};

  const isEmpty = (v: unknown) =>
    v === undefined || v === null || String(v).trim() === "";

  const requiredFields: (keyof CustomerDetail)[] = [
    "name",
    "entity_name",
    "designation",
    "industry_type",
    "lead_rm",
    "company_type_id",
    "source",
  ];

  if (!isEdit) {
    requiredFields.push("deal_size", "tenure");
  }

  requiredFields.forEach((field) => {
    if (isEmpty(formData[field])) {
      newErrors[field as string] = "This field is required";
    }
  });

  if (!isEdit) {
    if (!requirementType?.trim()) {
      newErrors.requirement_type = "This field is required";
    }
    if (!dealTypeId && isEmpty(formData.deal_type)) {
      newErrors.deal_type = "This field is required";
    }
  }

  const emailStr = String(formData.email ?? "").trim();
  const phoneStr = String(formData.phone ?? "").trim();
  const hasEmail = !isEmpty(emailStr);
  const hasPhone = !isEmpty(phoneStr);

  if (!hasEmail && !hasPhone) {
    newErrors.email = "Enter email or phone";
    newErrors.phone = "Enter email or phone";
  } else {
    if (hasEmail && !EMAIL_REGEX.test(emailStr)) {
      newErrors.email = "Invalid email format";
    }
    if (hasPhone && !/^\d{10,15}$/.test(phoneStr)) {
      newErrors.phone = "Enter a valid phone number (10–15 digits)";
    }
  }

  if (showPanField) {
    if (!panTrimmed) {
      newErrors.pan = "PAN is required for this company type";
    } else if (!INDIAN_PAN_REGEX.test(panTrimmed.toUpperCase())) {
      newErrors.pan = "Enter a valid PAN (5 letters, 4 digits, 1 letter)";
    }
  }

  if (
    !isEdit &&
    formData.entity_name &&
    !formData.entity_id &&
    !entityNameFromPnpFetch
  ) {
    newErrors.entity_name =
      "Please select an entity from the dropdown suggestions";
  }

  return newErrors;
}

const AddCustomerPopup: React.FC<AddCustomerPopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  admin_users,
  lender_user_product_types,
  isEdit = false,
  customerId = null,
  initialData = null,
}) => {
  const popupRef = React.useRef<HTMLDivElement>(null);
  const entityDropdownRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<Partial<CustomerDetail>>({
    name: "",
    entity_name: "",
    lead_rm: "",
    deal_size: "",
    source: "",
    deal_type: "",
    designation: "",
    email: "",
    phone: "",
    industry_type: "",
    entity_id: "",
    entity_id_type: "",
    tenure: "",
    company_type_id: "",
    pan: "",
    pan_base_details: null,
  });

  const [entitySuggestions, setEntitySuggestions] = useState<
    Array<{ id: string; idType: string; name: string; status: string }>
  >([]);
  const [showEntitySuggestions, setShowEntitySuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [industryTypes, setIndustryTypes] = useState<
    Array<{ id: number; name: string; displayOrder: number; isActive: number }>
  >([]);

  const [requirementType, setRequirementType] = useState<string>(""); // selected parent
  const [dealTypeId, setDealTypeId] = useState<string>(""); // single selected deal type
  const [companyTypes, setCompanyTypes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [assetCategories, setAssetCategories] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [assetCategoryId, setAssetCategoryId] = useState<string>("");
  const [pnpFetchLoading, setPnpFetchLoading] = useState(false);
  /** When set, entity name came from PAN fetch so submit is allowed without picking from search */
  const [entityNameFromPnpFetch, setEntityNameFromPnpFetch] = useState(false);
  // Unique parent types
  const parentTypes = useMemo(
    () =>
      Array.from(new Set(lender_user_product_types.map((t) => t.parent_type))),
    [lender_user_product_types] // run whenever data changes
  );

  // Products for selected parent
  const filteredDeals = useMemo(
    () =>
      lender_user_product_types.filter((t) =>
        requirementType ? t.parent_type === requirementType : true
      ),
    [requirementType, lender_user_product_types] // include lender_user_product_types
  );

  const selectedCompanyTypeId = Number(formData.company_type_id);
  const showPanField =
    selectedCompanyTypeId === 4 || selectedCompanyTypeId === 5;
  const panTrimmed = (formData.pan || "").trim();
  const panNorm = panTrimmed.toUpperCase();
  const isPanFormatValid =
    panNorm.length === 10 && INDIAN_PAN_REGEX.test(panNorm);
  const showPnpFetchButton = showPanField && isPanFormatValid;

  const isFormComplete = useMemo(
    () =>
      Object.keys(
        collectFormValidationErrors({
          formData,
          isEdit,
          requirementType,
          dealTypeId,
          showPanField,
          panTrimmed,
          entityNameFromPnpFetch,
        })
      ).length === 0,
    [
      formData,
      isEdit,
      requirementType,
      dealTypeId,
      showPanField,
      panTrimmed,
      entityNameFromPnpFetch,
    ]
  );

  // Fetch industry types when component mounts
  useEffect(() => {
    const fetchIndustryTypes = async () => {
      try {
        const response = await apiService.get<{
          success: boolean;
          status: number;
          data: Array<{
            id: number;
            name: string;
            displayOrder: number;
            isActive: number;
          }>;
          message: string;
        }>("/customers/industry-types");
        if (response.success && response.data && Array.isArray(response.data)) {
          setIndustryTypes(response.data);
        }

        const entity = await apiService.get<{ data?: Array<{ id: number; name: string }> }>(
          `/supplier/company-types`
        );
        if (Array.isArray(entity.data)) {
          setCompanyTypes(entity.data);
        }

        const categories = await apiService.get<{
          data: Array<{ id: number; name: string }>;
        }>("/lender/get-asset-categories-list");
        if (Array.isArray(categories.data)) {
          setAssetCategories(categories.data);
        }
      } catch (error) {
        console.error("Error fetching industry types:", error);
        // Fallback to constants if API fails
        setIndustryTypes(
          CustomerValidEntityTypes.map((type, index) => ({
            id: index + 1,
            name: type,
            displayOrder: index + 1,
            isActive: 1,
          }))
        );
      }
    };

    fetchIndustryTypes();
  }, []);

  useEffect(() => {
    return () => {
      // Clean up any pending timeouts when component unmounts
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Add this useEffect to handle outside clicks for entity dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        entityDropdownRef.current &&
        !entityDropdownRef.current.contains(event.target as Node)
      ) {
        setShowEntitySuggestions(false);
      }
    }

    // Only add the listener when the dropdown is shown
    if (showEntitySuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEntitySuggestions]);

  // Initialize with initialData if provided
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  // Fetch customer details when editing
  useEffect(() => {
    if (isOpen && isEdit && customerId) {
      fetchCustomerDetails();
    } else if (isOpen && !isEdit) {
      // Reset form for new customer
      setFormData({
        name: "",
        entity_name: "",
        lead_rm: "",
        deal_size: "",
        source: "",
        deal_type: "",
        designation: "",
        email: "",
        phone: "",
        industry_type: "",
        entity_id: "",
        entity_id_type: "",
        tenure: "",
        company_type_id: "",
        pan: "",
        pan_base_details: null,
      });
      setRequirementType("");
      setDealTypeId("");
      setAssetCategoryId("");
      setShowEntitySuggestions(false);
      setEntitySuggestions([]);
      setIsSearching(false);
      setErrors({});
      setEntityNameFromPnpFetch(false);
    }
  }, [isOpen, isEdit, customerId]);

  const fetchCustomerDetails = async () => {
    if (!customerId) return;

    setLoading(true);
    try {
      const response = await apiService.get<any>(
        `/customers/customer-details/${customerId}`
      );

      setFormData({
        name: response.data.name || "",
        entity_name: response.data.entity_name || "",
        lead_rm: String(response.data.lead_rm || ""),
        deal_size: String(response.data.deal_size || ""),
        source: response.data.source || "",
        deal_type: String(response.data.deal_type || ""),
        designation: response.data.designation || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        industry_type: response.data.industry_type || "",
        company_type_id: response.data.company_type_id != null
          ? String(response.data.company_type_id)
          : "",
        pan: response.data.pan != null ? String(response.data.pan) : "",
        tenure: String(response.data.tenure ?? ""),
        pan_base_details: response.data.pan_base_details ?? null,
      });
    } catch (error) {
      console.error("Error fetching customer details:", error);
      toast.error("Failed to load customer details");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field is changed; email/phone share an "at least one" rule
    if (field === "email" && (errors.email || errors.phone)) {
      setErrors((prev) => ({ ...prev, email: "", phone: "" }));
    } else if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 15);
    setFormData((prev) => ({ ...prev, phone: digitsOnly }));
    if (errors.phone || errors.email) {
      setErrors((prev) => ({ ...prev, phone: "", email: "" }));
    }
  };

  const handleParentChange = (value: string) => {
    setRequirementType(value);
    setDealTypeId(""); // reset deal type when parent changes
    setAssetCategoryId("");

    setFormData((prev) => ({
      ...prev,
      requirement_type: value,
      deal_type: "", // reset deal_type in formData
      asset_category_id: null,
    }));

    // Clear errors if any
    if (errors.requirement_type || errors.deal_type) {
      setErrors((prev) => ({
        ...prev,
        requirement_type: "",
        deal_type: "",
      }));
    }
  };

  const handleAssetCategoryChange = (value: string) => {
    setAssetCategoryId(value);
    setFormData((prev) => ({
      ...prev,
      asset_category_id: value ? Number(value) : null,
    }));
  };

  // Deal Type change (single select)
  const handleDealChange = (selectedId: string) => {
    setDealTypeId(selectedId);
    setFormData((prev) => ({
      ...prev,
      deal_type: selectedId,
    }));

    // Clear errors
    if (errors.deal_type) {
      setErrors((prev) => ({
        ...prev,
        deal_type: "",
      }));
    }
  };

  const handleCompanyTypeChange = (value: string) => {
    const typeNum = Number(value);
    const panApplicable = typeNum === 4 || typeNum === 5;
    setFormData((prev) => ({
      ...prev,
      company_type_id: value,
      pan: panApplicable ? prev.pan ?? "" : "",
      pan_base_details: panApplicable ? prev.pan_base_details ?? null : null,
    }));
    if (typeNum !== 4 && typeNum !== 5) {
      setEntityNameFromPnpFetch(false);
    }
    if (panApplicable) {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      setShowEntitySuggestions(false);
      setEntitySuggestions([]);
      setIsSearching(false);
    }
    if (errors.company_type_id) {
      setErrors((prev) => ({ ...prev, company_type_id: "" }));
    }
    if (errors.pan) {
      setErrors((prev) => ({ ...prev, pan: "" }));
    }
  };

  const handlePanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 10);
    setFormData((prev) => ({
      ...prev,
      pan: v,
      pan_base_details: null,
      ...(entityNameFromPnpFetch
        ? { entity_name: "", entity_id: "", entity_id_type: "" }
        : {}),
    }));
    setEntityNameFromPnpFetch(false);
    setErrors((prev) => {
      const next = { ...prev };
      if (v.length === 0 || v.length < 10) {
        delete next.pan;
        return next;
      }
      if (!INDIAN_PAN_REGEX.test(v)) {
        next.pan = "Enter a valid PAN (5 letters, 4 digits, 1 letter)";
        return next;
      }
      delete next.pan;
      return next;
    });
  };

  const handleFetchPnpDetails = async () => {
    const pan = panTrimmed.toUpperCase();
    if (!pan || !INDIAN_PAN_REGEX.test(pan)) {
      setErrors((prev) => ({
        ...prev,
        pan: !pan
          ? "PAN is required"
          : "Enter a valid PAN (5 letters, 4 digits, 1 letter)",
      }));
      toast.error(!pan ? "Enter PAN" : "Enter a valid PAN");
      return;
    }
    setPnpFetchLoading(true);
    try {
      const res = await apiService.post<Record<string, unknown>>(`/customers/pnp-base-details`, {
        pan,
      });
      const legalName = extractPnpLegalName(res);
      setFormData((prev) => ({
        ...prev,
        pan_base_details: res,
        ...(legalName
          ? {
              entity_name: legalName,
              entity_id: "",
              entity_id_type: "",
            }
          : {}),
      }));
      if (legalName) {
        setEntityNameFromPnpFetch(true);
        setErrors((prev) =>
          prev.entity_name ? { ...prev, entity_name: "" } : prev
        );
      } else {
        setEntityNameFromPnpFetch(false);
      }
      const message =
        typeof res?.message === "string" && res.message
          ? res.message
          : "PAN details fetched successfully";
      toast.success(message);
    } catch (error: unknown) {
      const err = error as { message?: string; error?: string };
      toast.error(
        typeof err?.message === "string"
          ? err.message
          : typeof err?.error === "string"
            ? err.error
            : "Failed to fetch PAN details"
      );
    } finally {
      setPnpFetchLoading(false);
    }
  };

  /** Unlock PAN / company type / entity after a successful lookup so user can start over */
  const handleResetPanLookup = () => {
    setEntityNameFromPnpFetch(false);
    setFormData((prev) => ({
      ...prev,
      pan: "",
      entity_name: "",
      entity_id: "",
      entity_id_type: "",
      pan_base_details: null,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.pan;
      delete next.entity_name;
      delete next.pan_base_details;
      return next;
    });
  };

  const validateForm = (): boolean => {
    const newErrors = collectFormValidationErrors({
      formData,
      isEdit,
      requirementType,
      dealTypeId,
      showPanField,
      panTrimmed,
      entityNameFromPnpFetch,
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const isLeasing = requirementType === "Leasing";
      const success = await onSubmit({
        ...formData,
        id: customerId || undefined, // Include ID when editing
        pan_base_details: showPanField
          ? formData.pan_base_details ?? null
          : null,
        asset_category_id:
          isLeasing && assetCategoryId ? Number(assetCategoryId) : null,
      });

      if (success) {
        onClose();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/20 z-[400] flex items-center justify-center overflow-auto p-4">
      <div
        ref={popupRef}
        className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg shadow-lg p-6 animate-fade-in-scale"
      >
        <div className="flex items-center justify-between mb-4 pb-2 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {isEdit ? "Edit Customer" : "Add Customer"}
          </h2>
          <button
            onClick={() => {
              if (!loading) onClose();
            }}
            className="text-muted-foreground hover:text-foreground disabled:opacity-50"
            aria-label="Close"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="grid grid-cols-2 gap-x-5 gap-y-4 items-start py-1">
              {/* Left Column */}
              {/* <div className="space-y-4"> */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Industry Type <span className="text-destructive">*</span>
                </label>
                <DropdownFilter
                  className="rounded-md border border-slate-300 bg-white p-[2px] w-full"
                  options={industryTypes.map((type) => ({
                    label: type.name,
                    value: type.id,
                  }))}
                  selectedOptions={industryTypes
                    .filter(
                      (type) => type.id === Number(formData.industry_type)
                    )
                    .map((type) => ({ label: type.name, value: type.id }))}
                  onChange={(selected) => {
                    const selectedId =
                      selected[1]?.value ?? selected[0]?.value ?? "";
                    handleInputChange("industry_type", String(selectedId));
                  }}
                  placeholder="Select industry type"
                  fullWidth={true}
                  triggerClassName={
                    errors.industry_type
                      ? "border-red-500"
                      : "border-none text-left"
                  }
                />
                {errors.industry_type && (
                  <p className="text-[11px] text-red-500 mt-1">
                    {errors.industry_type}
                  </p>
                )}
              </div>


              <div className="space-y-3 min-w-0">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Company Type <span className="text-destructive">*</span>
                  </label>
                  <Select
                    value={formData.company_type_id || ""}
                    onValueChange={handleCompanyTypeChange}
                    disabled={entityNameFromPnpFetch}
                  >
                    <SelectTrigger
                      className={
                        errors.company_type_id
                          ? "border-red-500"
                          : entityNameFromPnpFetch
                            ? "opacity-80"
                            : ""
                      }
                    >
                      <SelectValue placeholder="Select company type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-60 overflow-y-auto z-[500]">
                      {companyTypes.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.company_type_id && (
                    <p className="text-[11px] text-red-500 mt-1">
                      {errors.company_type_id}
                    </p>
                  )}
                </div>

                
              </div>
              {/* <div> */}
                {showPanField && (
                  <div className="space-y-1.5 pt-0.5 border-border/60">
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      PAN <span className="text-destructive">*</span>
                    </label>
                    <div className="flex flex-row flex-wrap items-center gap-2">
                      <div className="min-w-0 flex-1 basis-[min(100%,12rem)]">
                        <InputWithIcon
                          icon={<IdCard className="h-4 w-4" />}
                          className="w-full"
                          value={formData.pan || ""}
                          onChange={handlePanChange}
                          placeholder="e.g. ABCDE1234F"
                          maxLength={10}
                          disabled={pnpFetchLoading || entityNameFromPnpFetch}
                          hasError={Boolean(errors.pan)}
                        />
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {showPnpFetchButton && !entityNameFromPnpFetch && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 border-[#3f8b98] text-[#3f8b98] hover:bg-[#3f8b98]/10"
                            disabled={pnpFetchLoading}
                            onClick={() => void handleFetchPnpDetails()}
                          >
                            {pnpFetchLoading ? (
                              <span className="flex items-center gap-2">
                                <span className="animate-spin rounded-full h-4 w-4 border-2 border-[#3f8b98] border-t-transparent" />
                                Fetching...
                              </span>
                            ) : (
                              "Fetch"
                            )}
                          </Button>
                        )}
                        {entityNameFromPnpFetch && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 whitespace-nowrap text-muted-foreground hover:text-foreground"
                            disabled={pnpFetchLoading}
                            onClick={handleResetPanLookup}
                          >
                            Change PAN
                          </Button>
                        )}
                      </div>
                    </div>
                    {errors.pan && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors.pan}
                      </p>
                    )}
                  </div>
                )}
              {/* </div> */}

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Entity Name <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <InputWithIcon
                    icon={<Building2 className="h-4 w-4" />}
                    disabled={isEdit || entityNameFromPnpFetch}
                    value={formData.entity_name || ""}
                    onChange={(e) => {
                      const value = e.target.value;

                      // If not in edit mode, clear entity_id when user types manually
                      // This enforces selection from dropdown
                      if (!isEdit) {
                        setEntityNameFromPnpFetch(false);
                        // Clear entity_id if user is typing (not selecting from dropdown)
                        if (formData.entity_id) {
                          setFormData((prev) => ({
                            ...prev,
                            entity_id: "",
                            entity_id_type: "",
                          }));
                        }
                      }

                      handleInputChange("entity_name", value);

                      const companyTypeId = Number(formData.company_type_id);
                      const skipCompanySearch =
                        companyTypeId === 4 || companyTypeId === 5;

                      // Call search API after 3 characters (not for company types 4 / 5)
                      if (
                        value.trim().length > 3 &&
                        !entityNameFromPnpFetch &&
                        !skipCompanySearch
                      ) {
                        // Use debounce to prevent multiple API calls
                        if (searchTimeout.current) {
                          clearTimeout(searchTimeout.current);
                        }

                        searchTimeout.current = setTimeout(() => {
                          setIsSearching(true);
                          apiService
                            .post(`/customers/company/search`, { query: value })
                            .then((response) => {
                              setEntitySuggestions(
                                (response as { data: any[] }).data || []
                              );
                              setShowEntitySuggestions(true);
                              setIsSearching(false);
                            })
                            .catch((error) => {
                              console.error("Error searching entities:", error);
                              setIsSearching(false);
                            });
                        }, 300); // Wait 0.3 second after typing stops
                      } else {
                        setShowEntitySuggestions(false);
                        // Clear suggestions if less than 3 characters
                        setEntitySuggestions([]);
                      }
                    }}
                    placeholder={
                      isEdit
                        ? "Enter entity name"
                        : entityNameFromPnpFetch
                          ? "Company name from PAN"
                          : "Type to search and select entity"
                    }
                    hasError={Boolean(errors.entity_name)}
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                  {showEntitySuggestions && entitySuggestions.length > 0 && (
                    <div
                      ref={entityDropdownRef}
                      className="absolute z-[500] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto overflow-x-auto"
                    >
                      {entitySuggestions.map((entity, index) => (
                        <div
                          key={index}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center text-xs min-w-max"
                          onClick={() => {
                            // First check for duplication
                            setIsSearching(true);
                            apiService
                              .get(
                                `/customers/check-duplicate?entity_id=${entity.id}`
                              )
                              .then((response) => {
                                if (
                                  (response as { data: { exists: boolean } })
                                    .data.exists
                                ) {
                                  toast.error(
                                    "This entity already exists in the system"
                                  );
                                  // Clear the input if duplicate
                                  handleInputChange("entity_name", "");
                                  setEntityNameFromPnpFetch(false);
                                  setFormData((prev) => ({
                                    ...prev,
                                    entity_id: "",
                                    entity_id_type: "",
                                  }));
                                  return;
                                }
                                // If no duplication, proceed with selection
                                setEntityNameFromPnpFetch(false);
                                handleInputChange("entity_name", entity.name);
                                setFormData((prev) => ({
                                  ...prev,
                                  entity_id: entity.id,
                                  entity_id_type: entity.idType,
                                }));
                                setShowEntitySuggestions(false);
                                // Clear any existing errors
                                if (errors.entity_name) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    entity_name: "",
                                  }));
                                }
                              })
                              .catch((error) => {
                                console.error(
                                  "Error checking entity duplication:",
                                  error
                                );
                                toast.error(
                                  "Error checking entity duplication"
                                );
                              })
                              .finally(() => {
                                setIsSearching(false);
                              });
                          }}
                        >
                          <div className="flex w-full min-w-max">
                            <span
                              className={`w-2 h-2 rounded-full shrink-0 ${
                                entity.status === "ACTIVE"
                                  ? "bg-green-500"
                                  : "bg-red-500"
                              }`}
                            ></span>
                            <p className="whitespace-nowrap pl-2">
                              {entity.name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showEntitySuggestions &&
                    entitySuggestions.length === 0 &&
                    formData.entity_name &&
                    formData.entity_name.length > 3 &&
                    !isSearching && (
                      <div className="absolute z-[500] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 text-xs text-gray-500">
                        No entities found. Please select from the suggestions
                        above.
                      </div>
                    )}
                </div>
                {errors.entity_name && (
                  <p className="text-[11px] text-red-500 mt-1">
                    {errors.entity_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Customer Name <span className="text-destructive">*</span>
                </label>
                <InputWithIcon
                  icon={<User className="h-4 w-4" />}
                  value={formData.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter customer name"
                  hasError={Boolean(errors.name)}
                />
                {errors.name && (
                  <p className="text-[11px] text-red-500 mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Designation <span className="text-destructive">*</span>
                </label>
                {CustomerValidDesignations.length > 0 && (
                  <Select
                    value={formData.designation?.toString() || ""}
                    onValueChange={(value) =>
                      handleInputChange("designation", value)
                    }
                  >
                    <SelectTrigger
                      className={errors.designation ? "border-red-500" : ""}
                    >
                      <SelectValue placeholder="Select designation" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-60 overflow-y-auto z-[500]">
                      {CustomerValidDesignations.map((designation) => (
                        <SelectItem key={designation} value={designation}>
                          {designation}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {errors.designation && (
                  <p className="text-[11px] text-red-500 mt-1">
                    {errors.designation}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Email
                </label>
                <InputWithIcon
                  icon={<Mail className="h-4 w-4" />}
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="Enter email"
                  hasError={Boolean(errors.email)}
                />
                {errors.email && (
                  <p className="text-[11px] text-red-500 mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Phone
                </label>
                <InputWithIcon
                  icon={<Phone className="h-4 w-4" />}
                  value={formData.phone || ""}
                  onChange={handlePhoneChange}
                  placeholder="Enter phone"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={15}
                  hasError={Boolean(errors.phone)}
                />
                {errors.phone && (
                  <p className="text-[11px] text-red-500 mt-1">{errors.phone}</p>
                )}
              </div>

              {/* ---------------- */}

              {/* Parent Type */}
              {!isEdit && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Requirement Type <span className="text-destructive">*</span>{" "}
                    </label>
                    <Select
                      value={requirementType}
                      onValueChange={handleParentChange}
                    >
                      <SelectTrigger
                        className={
                          errors.requirement_type ? "border-red-500" : ""
                        }
                      >
                        <SelectValue placeholder="Select Requirement type" />
                      </SelectTrigger>
                      <SelectContent className="z-[500]">
                        {parentTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.requirement_type && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors.requirement_type}
                      </p>
                    )}
                  </div>

                  {/* Deal Type Single-Select Dropdown */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">
                      Deal Type <span className="text-destructive">*</span>{" "}
                    </label>
                    <DropdownFilter
                      options={filteredDeals.map((p) => ({
                        label: p.name,
                        value: p.id.toString(),
                      }))}
                      selectedOptions={
                        dealTypeId
                          ? [
                              (() => {
                                const deal = lender_user_product_types.find(
                                  (d) => String(d.id) === String(dealTypeId)
                                );
                                return deal
                                  ? {
                                      label: deal.name,
                                      value: deal.id.toString(),
                                    }
                                  : { label: dealTypeId, value: dealTypeId };
                              })(),
                            ]
                          : []
                      }
                      onChange={(selected) => {
                        const last = selected[selected.length - 1];
                        handleDealChange(last ? last.value.toString() : "");
                      }}
                      placeholder={
                        requirementType
                          ? "Select Deal Type"
                          : "Select Parent Type first"
                      }
                      fullWidth={true}
                      disabled={!requirementType} // ✅ disable if parent not selected
                      triggerClassName={
                        errors.deal_type ? "border-red-500" : undefined
                      }
                    />
                    {errors.deal_type && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors.deal_type}
                      </p>
                    )}
                  </div>

                  {requirementType === "Leasing" && (
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                        Asset Category
                      </label>
                      <Select
                        value={assetCategoryId}
                        onValueChange={handleAssetCategoryChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select asset category" />
                        </SelectTrigger>
                        <SelectContent className="z-[500]">
                          {assetCategories.map((category) => (
                            <SelectItem
                              key={category.id}
                              value={String(category.id)}
                            >
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* ------------------------ */}
                  {/* 
              <div>
                <label className="block text-sm font-medium mb-1">
                  Deal Type <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.deal_type || ""}
                  onValueChange={(value) => handleInputChange("deal_type", value)}
                >
                  <SelectTrigger className={errors.deal_type ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select deal type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-[500]">
                    {lender_user_product_types.map((type) => (
                      <SelectItem key={type.id} value={String(type.id)}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.deal_type && (
                  <p className="text-red-500 text-xs mt-1">{errors.deal_type}</p>
                )}
              </div> */}

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Deal Size <span className="text-destructive">*</span>
                    </label>
                    <InputWithIcon
                      icon={<IndianRupee className="h-4 w-4" />}
                      type="number"
                      value={formData.deal_size || ""}
                      onChange={(e) => {
                        // Limit to 20 digits
                        const value = e.target.value;
                        if (
                          value === "" ||
                          (value.length <= 19 && /^\d+$/.test(value))
                        ) {
                          handleInputChange("deal_size", value);
                        }
                      }}
                      placeholder="Enter deal size"
                      hasError={Boolean(errors.deal_size)}
                      min="0"
                      max="999999999999999999"
                    />
                    {errors.deal_size && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors.deal_size}
                      </p>
                    )}
                  </div>

                  <div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                        Tenure <span className="text-destructive">*</span>
                      </label>
                      <InputWithIcon
                        icon={<CalendarClock className="h-4 w-4" />}
                        type="number"
                        value={formData.tenure || ""}
                        onChange={(e) => {
                          // Limit to 20 digits
                          const value = e.target.value;
                          if (
                            value === "" ||
                            (value.length <= 19 && /^\d+$/.test(value))
                          ) {
                            handleInputChange("tenure", value);
                          }
                        }}
                        placeholder="months"
                        hasError={Boolean(errors.tenure)}
                        min="0"
                        max="9999"
                      />
                      {errors.tenure && (
                        <p className="text-[11px] text-red-500 mt-1">
                          {errors.tenure}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Source <span className="text-destructive">*</span>
                </label>
                <Select
                  value={formData.source || ""}
                  onValueChange={(value) => handleInputChange("source", value)}
                >
                  <SelectTrigger
                    className={errors.source ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-[500]">
                    {[
                      "Referral",
                      "Social Media",
                      "Inbound",
                      "Outbound",
                      "Past Relationship",
                    ].map((source) => (
                      <SelectItem key={source} value={source}>
                        {source}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.source && (
                  <p className="text-[11px] text-red-500 mt-1">{errors.source}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Lead RM <span className="text-destructive">*</span>
                </label>
                <Select
                  value={String(formData.lead_rm || "")}
                  onValueChange={(value) => handleInputChange("lead_rm", value)}
                >
                  <SelectTrigger
                    className={errors.lead_rm ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select lead RM" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {admin_users.map((rm) => (
                      <SelectItem key={rm.id} value={String(rm.id)}>
                        {rm.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.lead_rm && (
                  <p className="text-[11px] text-red-500 mt-1">{errors.lead_rm}</p>
                )}
              </div>
              {/* </div> */}
            </div>

            <div className="pt-2 border-t border-border flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !isFormComplete}
                size="sm"
                className="bg-[#3f8b98] text-white hover:bg-[#357784] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    {isEdit ? "Updating..." : "Submitting..."}
                  </div>
                ) : isEdit ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddCustomerPopup;
